<template>
  <v-data-table
      :headers="headers"
      :items="exams"
      item-key="key"
      :search="search"
      :custom-filter="filterExams"
      :loading="loading"
      :items-per-page="25"
      :footer-props="{itemsPerPageOptions: [25, 50, 100, -1], itemsPerPageText: 'Zeilen pro Seite'}"
      loading-text="Daten werden geladen..."
      :no-data-text="noDataText"
      no-results-text="Keine Treffer">
    <template v-slot:[`item.date`]="{ item }">
      {{ item.date | moment("DD.MM.YYYY") }}
    </template>
    <template v-slot:[`item.absence_type`]="{ item }">
      <div class="py-1">
        <v-chip small dark :color="absence(item).colour">
          {{ absence(item).label }}
        </v-chip>
        <div v-if="awaitsMainCertificate(item)" class="text-caption text--secondary">{{ statusDetail(item) }}</div>
      </div>
    </template>
    <template v-slot:[`item.status`]="{ item }">
      <div class="py-1">
        <v-chip small dark :color="status(item).colour">
          {{ status(item).label }}
        </v-chip>
        <v-chip v-if="item.missed_retakes" small dark color="grey" class="ml-1">
          {{ item.missed_retakes }}× Termin verpasst
        </v-chip>
        <div v-if="statusDetail(item)" class="text-caption text--secondary">{{ statusDetail(item) }}</div>
      </div>
    </template>
    <template v-slot:[`item.actions`]="{ item }">
      <v-menu bottom left>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon :loading="saving[item.key]" v-bind="attrs" title="Aktionen" v-on="on">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item v-if="item.admittable" @click="$emit('admit', item)">
            <v-list-item-icon><v-icon>mdi-account-plus</v-icon></v-list-item-icon>
            <v-list-item-title>Für Nachschreibetermin anmelden</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="item.admitted && !item.written" @click="$emit('withdraw-admission', item)">
            <v-list-item-icon><v-icon>mdi-account-remove</v-icon></v-list-item-icon>
            <v-list-item-title>Anmeldung zurücknehmen</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="!item.written" @click="$emit('record-separate', item)">
            <v-list-item-icon><v-icon>mdi-calendar-check</v-icon></v-list-item-icon>
            <v-list-item-title>Außerplanmäßig nachgeschrieben …</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="item.written_separately" @click="$emit('remove-separate', item)">
            <v-list-item-icon><v-icon>mdi-undo</v-icon></v-list-item-icon>
            <v-list-item-title>Außerplanmäßiges Nachschreiben zurücknehmen</v-list-item-title>
          </v-list-item>
          <template v-if="!archived && isAcknowledgeable(item)">
            <v-divider/>
            <v-list-item @click="$emit('archive', item)">
              <v-list-item-icon><v-icon>mdi-archive-arrow-down</v-icon></v-list-item-icon>
              <v-list-item-title>Zur Kenntnis genommen</v-list-item-title>
            </v-list-item>
          </template>
          <template v-if="archived">
            <v-divider/>
            <v-list-item @click="$emit('restore', item)">
              <v-list-item-icon><v-icon>mdi-archive-arrow-up</v-icon></v-list-item-icon>
              <v-list-item-title>Archivieren rückgängig machen</v-list-item-title>
            </v-list-item>
          </template>
        </v-list>
      </v-menu>
    </template>
  </v-data-table>
</template>

<script lang="ts">
import {Component, Prop, Vue} from "vue-property-decorator";
import {accentInsensitiveTableFilter} from "@/core/utils/search";
import {ABSENCE_TYPES, isAcknowledgeable, MissingExam, STATUSES} from "@/core/models/missing-exam";

// The teacher's missed exams; the actions are carried out by the parent.
@Component
export default class MissingExamList extends Vue {
  @Prop({required: true}) readonly exams!: Array<MissingExam & { key: string }>
  @Prop({type: Boolean, default: false}) readonly archived!: boolean
  @Prop({default: false}) readonly loading!: boolean
  @Prop({default: ''}) readonly search!: string | null
  @Prop({default: () => ({})}) readonly saving!: Record<string, boolean>
  @Prop({default: 'Keine versäumten Klausuren'}) readonly noDataText!: string

  private filterExams = accentInsensitiveTableFilter
  private isAcknowledgeable = isAcknowledgeable
  private headers: Array<Record<string, any>> = [
    {text: 'Schüler', value: 'student_name'},
    {text: 'Klasse', value: 'class_name'},
    {text: 'Klausur', value: 'subject_name'},
    {text: 'Datum', value: 'date'},
    {text: 'Stunde', value: 'lessons', sortable: false},
    {text: 'Status Haupttermin', value: 'absence_type'},
    {text: 'Status Nachschreibetermin', value: 'status', filterable: false},
    {text: '', value: 'actions', sortable: false, filterable: false, align: 'end'},
  ]

  private status(exam: MissingExam): { label: string, colour: string } {
    return STATUSES[exam.status] ?? {label: exam.status, colour: 'grey'}
  }

  private statusDetail(exam: MissingExam): string {
    switch (exam.status) {
      case 'written':
        return `am ${this.$moment(exam.written_on).format('DD.MM.YYYY')}${exam.written_separately ? ' (außerplanmäßig)' : ''}`
      case 'certificate_due':
        return `Attest bis ${this.$moment(exam.certificate_deadline).format('DD.MM.YYYY')}`
      case 'struck':
        return 'Zum Nachschreibetermin unentschuldigt gefehlt.'
      case 'unexcused':
        return 'Zum Haupttermin unentschuldigt gefehlt.'
      default:
        return exam.admitted ? 'Angemeldet' : ''
    }
  }

  // Until the certificate deadline an unexcused main date may still be excused, so it shows the wait as well.
  private absence(exam: MissingExam): { label: string, colour: string } {
    if (this.awaitsMainCertificate(exam)) {
      return STATUSES.certificate_due
    }

    return ABSENCE_TYPES[exam.absence_type] ?? {label: exam.absence_type, colour: 'grey'}
  }

  // Without an admission the certificate deadline counts from the main date, see MissingExam::certificateDeadline().
  private awaitsMainCertificate(exam: MissingExam): boolean {
    return exam.status === 'certificate_due' && !exam.admitted
  }
}
</script>
